import { useCurrency } from "../hooks/useCurrency";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  User, Mail, Shield, KeyRound, BarChart3, Copy, RefreshCw, Eye, EyeOff,
  Check, Zap, Loader2, Rocket, BadgeCheck, Code2, Settings2, Minus, Plus, XCircle, Receipt, Download, FileText, ShieldCheck, ShieldOff,
  Users, UserPlus, Trash2, Send, Link2, Clock,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail, downloadInvoice } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { QRCodeSVG } from "qrcode.react";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const PLAN_META = {
  basic: { blurb: "Free forever — the essentials", icon: Zap },
  plus: { blurb: "Branding, exports & all layouts", icon: BadgeCheck },
  api: { blurb: "Public REST API access", icon: Code2 },
};
const PLAN_FEATURES = {
  basic: ["Unlimited PNR conversions", "3 itinerary layouts", "Flight leg summary", "Personal and commercial use", "Download PDF"],
  plus: ["Everything in Basic", "10 itinerary ready to use layouts", "Full customisation & agency branding", "PDF, HTML & WhatsApp export", "Password-protected share links", "Priority email support"],
  api: ["Public REST /api/convert endpoint", "API key management", "Sabre & Amadeus parsing", "Enriched airlines, airports & logos"],
};
const TIER_PRICES = [24.99, 49.99, 99.99, 199.99];
const money = (n) => (n === 0 ? "0" : Number(n).toFixed(2));

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [billing, setBilling] = useState(null);  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [users, setUsers] = useState(1);
  const [apiTier, setApiTier] = useState(0);
  const [txns, setTxns] = useState([]);
  const [invoiceBusy, setInvoiceBusy] = useState("");
  const [twoFA, setTwoFA] = useState({ open: false, uri: "", secret: "", code: "", busy: false });
  const [pw, setPw] = useState({ open: false, current: "", next: "", confirm: "", busy: false });
  const [interval, setInterval] = useState("monthly");
  const { fmt } = useCurrency();

  const [team, setTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [teamBusy, setTeamBusy] = useState("");
  const [lastInvite, setLastInvite] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const isMember = billing?.is_member;
  const isPlusOwner = billing && !billing.is_member && billing.plan === "plus";

  const loadTeam = useCallback(async () => {
    try { const { data } = await api.get("/team"); setTeam(data); }
    catch (e) { /* not an owner / not plus */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [b, p, t] = await Promise.all([api.get("/billing/me"), api.get("/billing/plans"), api.get("/transactions")]);
      setBilling(b.data);
      setPlans(p.data);
      setUsers(b.data.users || 1);
      setApiTier(b.data.api_tier || 0);
      setInterval(b.data.billing_interval || "monthly");
      setTxns(t.data || []);
      if (!b.data.is_member && b.data.plan === "plus") loadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [loadTeam]);

  useEffect(() => { load(); }, [load]);

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(billing.api_key); } catch (e) { console.debug("clipboard copy failed", e); }
    setCopied(true); toast.success("API key copied."); setTimeout(() => setCopied(false), 1500);
  };

  const regenerate = async () => {
    setBusy("regen");
    try {
      const { data } = await api.post("/billing/regenerate-key");
      setBilling((b) => ({ ...b, api_key: data.api_key }));
      toast.success("New API key generated.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const applyBilling = (b) => {
    setBilling(b);
    setUsers(b.users || 1);
    setApiTier(b.api_tier || 0);
    setInterval(b.billing_interval || "monthly");
  };

  const downgradeToBasic = async () => {
    setBusy("basic");
    try {
      const { data } = await api.post("/billing/subscribe", { plan: "basic" });
      applyBilling(data.billing);
      try { const t = await api.get("/transactions"); setTxns(t.data || []); } catch (e) { console.debug("txn refresh failed", e); }
      refreshUser();
      setTeam(null);
      toast.success("You're now on the Basic (free) plan.");
      setManageOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const checkout = async (plan) => {
    if (plan === "basic") return downgradeToBasic();
    setBusy(plan);
    try {
      const body = { plan, interval };
      if (plan === "plus") body.users = users;
      if (plan === "api") body.api_tier = apiTier;
      const { data } = await api.post("/billing/checkout", body);
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) throw new Error("Could not load the payment window. Please try again.");
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "PNR Decode",
        description: `${data.plan_name} — ${data.interval === "annual" ? "Annual" : "Monthly"} (${data.currency} ${data.amount_display})`,
        prefill: data.prefill,
        theme: { color: "#1E90FF" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/billing/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            applyBilling(v.billing);
            try { const t = await api.get("/transactions"); setTxns(t.data || []); } catch (e) { console.debug("txn refresh failed", e); }
            refreshUser();
            if (v.billing.plan === "plus") loadTeam();
            toast.success(`Payment successful — you're now on ${v.billing.plan_name}.`);
            setManageOpen(false);
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed.");
          } finally { setBusy(""); }
        },
        modal: { ondismiss: () => setBusy("") },
      };
      const rz = new window.Razorpay(options);
      rz.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. Please try again.");
        setBusy("");
      });
      rz.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      setBilling(data.billing);
      toast.success("Subscription will end at the end of your billing period — no further charges.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const inviteMember = async (e) => {
    e?.preventDefault?.();
    setTeamBusy("invite"); setLastInvite(null);
    try {
      const { data } = await api.post("/team/invite", { email: inviteEmail.trim(), name: inviteName.trim(), base_url: window.location.origin });
      setInviteEmail(""); setInviteName("");
      setLastInvite({ email: data.member.email, link: data.invite_link, sent: data.email_sent });
      toast.success(data.email_sent ? `Invitation emailed to ${data.member.email}.` : `Invite created for ${data.member.email}.`);
      loadTeam();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTeamBusy(""); }
  };

  const resendInvite = async (m) => {
    setTeamBusy(`resend-${m.id}`);
    try {
      const { data } = await api.post(`/team/${m.id}/resend`, { base_url: window.location.origin });
      setLastInvite({ email: m.email, link: data.invite_link, sent: data.email_sent });
      toast.success(data.email_sent ? `Invitation re-sent to ${m.email}.` : `New invite link generated for ${m.email}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTeamBusy(""); }
  };

  const removeMember = async (m) => {
    setTeamBusy(`remove-${m.id}`);
    try {
      await api.delete(`/team/${m.id}`);
      toast.success(`${m.email} removed from your team.`);
      loadTeam();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTeamBusy(""); }
  };

  const copyInviteLink = async (link) => {
    try { await navigator.clipboard.writeText(link); } catch (e) { console.debug("clipboard copy failed", e); }
    setCopiedLink(true); toast.success("Invite link copied."); setTimeout(() => setCopiedLink(false), 1500);
  };

  if (!user) return null;

  const getInvoice = async (t) => {
    setInvoiceBusy(t.id);
    try {
      await downloadInvoice(t.id, `${t.invoice_no}.pdf`);
    } catch (e) {
      toast.error("Could not download invoice. Please try again.");
    } finally { setInvoiceBusy(""); }
  };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };

  const start2FA = async () => {
    setTwoFA((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post("/auth/totp/setup");
      setTwoFA({ open: true, uri: data.otpauth_uri, secret: data.secret, code: "", busy: false });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setTwoFA((s) => ({ ...s, busy: false })); }
  };
  const enable2FA = async () => {
    setTwoFA((s) => ({ ...s, busy: true }));
    try {
      await api.post("/auth/totp/enable", { code: twoFA.code });
      toast.success("Two-factor authentication enabled.");
      setTwoFA({ open: false, uri: "", secret: "", code: "", busy: false });
      refreshUser();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setTwoFA((s) => ({ ...s, busy: false })); }
  };
  const disable2FA = async () => {
    const code = window.prompt("Enter a current 6-digit code from your authenticator app to disable 2FA:");
    if (!code) return;
    try {
      await api.post("/auth/totp/disable", { code: code.trim() });
      toast.success("Two-factor authentication disabled.");
      refreshUser();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setPw((s) => ({ ...s, busy: true }));
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed successfully.");
      setPw({ open: false, current: "", next: "", confirm: "", busy: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPw((s) => ({ ...s, busy: false }));
    }
  };

  const maskedKey = billing?.api_key ? `${billing.api_key.slice(0, 12)}${"•".repeat(16)}` : "";
  const apiEnabled = billing?.api_enabled;
  const limitLabel = billing?.api_limit === null ? "Unlimited" : (billing?.api_limit || 0).toLocaleString();
  const usedPct = billing && billing.api_limit ? Math.min((billing.calls_used / billing.api_limit) * 100, 100) : 0;
  const endDate = billing?.plan_ends_at ? new Date(billing.plan_ends_at).toLocaleDateString() : "";
  const nextBilling = billing?.next_billing_date ? new Date(billing.next_billing_date).toLocaleDateString() : "";
  const statusText = billing
    ? (billing.cancel_at_period_end ? `Cancelling — access until ${endDate}`
      : billing.subscription_status === "active" ? "Active"
      : billing.plan === "basic" ? "Active (free)" : "Inactive")
    : "";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-1">Welcome back{user.name ? `, ${user.name}` : ""}.</p>

        {billing?.demo_mode && (
          <div data-testid="demo-banner" className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <strong>Demo billing:</strong> plan changes apply instantly without a real charge.
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Active plan */}
              <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="plan-card">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-slate-400" /> Your plan</h2>
                  <Badge data-testid="dash-plan" variant="outline" className="rounded-full border-blue-200 bg-blue-50 text-blue-700">{billing.plan_name}</Badge>
                </div>
                <div className="mt-4 font-heading text-3xl font-bold text-slate-900" data-testid="dash-plan-price">
                  {fmt(billing.price)}<span className="text-sm font-normal text-slate-400">{billing.plan === "plus" ? "/user/mo" : billing.plan === "basic" ? "" : "/mo"}</span>
                </div>
                {billing.plan === "plus" && !billing.is_member && (
                  <div data-testid="dash-plan-users" className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700">
                    <Users className="h-4 w-4" /> {billing.users}-user plan
                    {typeof billing.seats_used === "number" && <span className="text-slate-400 font-normal">· {billing.seats_used} of {billing.users} seat(s) in use</span>}
                  </div>
                )}
                {billing.is_member && (
                  <div data-testid="dash-member-note" className="mt-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800 inline-flex items-start gap-2">
                    <Users className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Team access provided by <b>{billing.team_owner_name || billing.team_owner_email}</b>. Your plan is managed by your account owner.</span>
                  </div>
                )}
                {billing.cancel_at_period_end ? (
                  <div data-testid="dash-status" className="mt-2 text-sm text-amber-700">Cancelling — access until {endDate}, then reverts to Basic.</div>
                ) : (
                  <div data-testid="dash-status" className="mt-2 text-sm text-slate-500 capitalize">Status: {billing.subscription_status === "none" ? "Active (free)" : billing.subscription_status}</div>
                )}
                {billing.plan !== "basic" && !billing.is_member && (
                  <div data-testid="dash-interval" className="mt-1 text-xs text-slate-400">
                    Billed {billing.billing_interval === "annual" ? "annually" : "monthly"}{nextBilling ? ` · next charge ${nextBilling}` : ""}
                  </div>
                )}
                <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-600" /> {billing.features.allowed_layouts === "all" ? "All 11 layouts" : `${billing.features.layouts} layouts`}</li>
                  <li className="flex items-center gap-2">{billing.features.branding ? <Check className="h-4 w-4 text-blue-600" /> : <XCircle className="h-4 w-4 text-slate-300" />} Agency branding</li>
                  <li className="flex items-center gap-2">{billing.features.share ? <Check className="h-4 w-4 text-blue-600" /> : <XCircle className="h-4 w-4 text-slate-300" />} Shareable links</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-600" /> Exports: {billing.features.exports.map((e) => e.toUpperCase()).join(", ")}</li>
                  <li className="flex items-center gap-2">{billing.features.api ? <Check className="h-4 w-4 text-blue-600" /> : <XCircle className="h-4 w-4 text-slate-300" />} Public REST API</li>
                </ul>
                <Button data-testid="manage-plan-btn" onClick={() => setManageOpen(true)} disabled={billing.is_member} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                  <Settings2 className="h-4 w-4 mr-1.5" /> {billing.is_member ? "Managed by owner" : "Manage plan"}
                </Button>
              </section>

              {/* API usage / key */}
              <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="apikey-card">
                <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><KeyRound className="h-4 w-4 text-slate-400" /> API access</h2>
                {apiEnabled ? (
                  <>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="font-heading text-2xl font-bold text-slate-900" data-testid="dash-calls-used">{billing.calls_used}</span>
                      <span className="text-slate-400 text-sm">/ {limitLabel} calls this month</span>
                    </div>
                    {billing.api_limit !== null && <Progress value={usedPct} className="mt-2 h-2" />}
                    <p className="text-sm text-slate-500 mt-3">Use this key in the <code className="font-pnr text-xs bg-slate-100 px-1 rounded">Authorization: Bearer</code> header.</p>
                    <div className="mt-3 flex items-center gap-2">
                      <code data-testid="dash-api-key" className="flex-1 font-pnr text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 truncate">{showKey ? billing.api_key : maskedKey}</code>
                      <Button data-testid="toggle-key-btn" variant="outline" size="icon" onClick={() => setShowKey((s) => !s)} className="border-slate-200 shrink-0">{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                      <Button data-testid="copy-key-btn" variant="outline" size="icon" onClick={copyKey} className="border-slate-200 shrink-0">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid="regenerate-key-btn" variant="ghost" size="sm" className="mt-3 text-slate-600" disabled={busy === "regen"}>
                          {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Regenerate key
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Regenerate API key?</AlertDialogTitle><AlertDialogDescription>Your current key stops working immediately.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-regenerate" onClick={regenerate} className="bg-blue-600 hover:bg-blue-700">Regenerate</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : (
                  <div className="mt-3 text-sm text-slate-500" data-testid="api-locked-note">
                    The public REST API is available on the <b>Conversion API</b> plan. Upgrade via Manage plan to get your key and call quota.
                  </div>
                )}
              </section>
            </div>

            {/* Team management (Plus owners) */}
            {isPlusOwner && (
              <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="team-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /> Team</h2>
                    <p className="text-sm text-slate-500 mt-1 max-w-md">Invite colleagues to your Plus workspace. Each teammate gets full app access — you keep control of billing and seats.</p>
                  </div>
                  {team && (
                    <Badge data-testid="team-seats" className="rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-sm px-3 py-1">
                      {team.used} / {team.seats} seats used
                    </Badge>
                  )}
                </div>

                {/* Invite form */}
                <form onSubmit={inviteMember} className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid="invite-form">
                  <div>
                    <label className="text-xs text-slate-500">Email</label>
                    <Input data-testid="invite-email-input" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@agency.com" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Name (optional)</label>
                    <Input data-testid="invite-name-input" value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Jane Doe" className="mt-1" />
                  </div>
                  <Button data-testid="invite-send-btn" type="submit" disabled={teamBusy === "invite" || (team && team.available <= 0)} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {teamBusy === "invite" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Invite</>}
                  </Button>
                </form>
                {team && team.available <= 0 && (
                  <p data-testid="no-seats-note" className="mt-2 text-xs text-amber-700">All seats are in use. Increase users in <button onClick={() => setManageOpen(true)} className="underline font-medium">Manage plan</button> to invite more.</p>
                )}

                {/* Last invite link (fallback if email didn't send) */}
                {lastInvite && (
                  <div data-testid="invite-link-box" className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div className="text-xs text-slate-500 mb-1">{lastInvite.sent ? `Invitation emailed to ${lastInvite.email}. You can also share this link:` : `Share this secure invite link with ${lastInvite.email}:`}</div>
                    <div className="flex items-center gap-2">
                      <code data-testid="invite-link-value" className="flex-1 font-pnr text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate">{lastInvite.link}</code>
                      <Button data-testid="copy-invite-link" variant="outline" size="icon" onClick={() => copyInviteLink(lastInvite.link)} className="border-slate-200 shrink-0">{copiedLink ? <Check className="h-4 w-4 text-emerald-600" /> : <Link2 className="h-4 w-4" />}</Button>
                    </div>
                  </div>
                )}

                {/* Member list */}
                <div className="mt-5">
                  {!team || team.members.length === 0 ? (
                    <div data-testid="no-members" className="flex flex-col items-center justify-center text-center py-8 rounded-lg border border-dashed border-slate-200">
                      <Users className="h-8 w-8 text-slate-300 mb-2" />
                      <div className="text-sm font-medium text-slate-600">No teammates yet</div>
                      <p className="text-xs text-slate-400 mt-1 max-w-xs">Invite someone above — they'll get {team ? team.available : 0} seat(s) to fill.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-testid="member-list">
                      {team.members.map((m) => (
                        <li key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{m.name || m.email}</div>
                            <div className="text-xs text-slate-500 truncate">{m.email}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {m.status === "active" ? (
                              <Badge className="rounded-full bg-emerald-100 text-emerald-700" data-testid={`member-status-${m.id}`}><Check className="h-3 w-3 mr-1" /> Active</Badge>
                            ) : (
                              <Badge variant="outline" className="rounded-full border-amber-200 bg-amber-50 text-amber-700" data-testid={`member-status-${m.id}`}><Clock className="h-3 w-3 mr-1" /> Invited</Badge>
                            )}
                            {m.status !== "active" && (
                              <Button data-testid={`member-resend-${m.id}`} variant="outline" size="sm" onClick={() => resendInvite(m)} disabled={teamBusy === `resend-${m.id}`} className="border-slate-200 text-slate-600">
                                {teamBusy === `resend-${m.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Resend</>}
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button data-testid={`member-remove-${m.id}`} variant="outline" size="icon" disabled={teamBusy === `remove-${m.id}`} className="border-red-200 text-red-600 hover:bg-red-50">
                                  {teamBusy === `remove-${m.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Remove {m.email}?</AlertDialogTitle><AlertDialogDescription>They'll immediately lose access and their seat is freed up. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid={`confirm-remove-${m.id}`} onClick={() => removeMember(m)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {/* Account */}
            <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="account-card">
              <h2 className="font-heading font-bold text-slate-900 mb-2">Account</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div><div className="text-slate-400 inline-flex items-center gap-1.5"><User className="h-4 w-4" /> Name</div><div data-testid="dash-name" className="font-medium text-slate-900 mt-0.5">{user.name || "—"}</div></div>
                <div><div className="text-slate-400 inline-flex items-center gap-1.5"><Mail className="h-4 w-4" /> Email</div><div data-testid="dash-email" className="font-medium text-slate-900 mt-0.5">{user.email}</div></div>
                <div><div className="text-slate-400 inline-flex items-center gap-1.5"><Shield className="h-4 w-4" /> Role</div><div data-testid="dash-role" className="font-medium text-slate-900 mt-0.5 capitalize">{user.role}</div></div>
              </div>
            </section>

            {/* Security / 2FA */}
            <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="security-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-slate-400" /> Two-factor authentication</h2>
                  <p className="text-sm text-slate-500 mt-1 max-w-md">Add an extra layer of security using an authenticator app (Google Authenticator, Authy). You'll enter a 6-digit code at sign-in.</p>
                </div>
                {user.totp_enabled ? (
                  <div className="flex items-center gap-3">
                    <Badge className="rounded-full bg-emerald-100 text-emerald-700" data-testid="twofa-status">Enabled</Badge>
                    <Button data-testid="twofa-disable" variant="outline" onClick={disable2FA} className="border-red-200 text-red-600 hover:bg-red-50">
                      <ShieldOff className="h-4 w-4 mr-1.5" /> Disable
                    </Button>
                  </div>
                ) : (
                  <Button data-testid="twofa-enable" onClick={start2FA} disabled={twoFA.busy || twoFA.open} className="bg-slate-900 hover:bg-slate-800 text-white">
                    {twoFA.busy && !twoFA.open ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> Enable 2FA</>}
                  </Button>
                )}
              </div>

              {twoFA.open && (
                <div className="mt-5 border-t border-slate-100 pt-5 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-start" data-testid="twofa-setup">
                  <div className="rounded-lg border border-slate-200 p-3 bg-white">
                    <QRCodeSVG value={twoFA.uri} size={168} data-testid="twofa-qr" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">1. Scan the QR code with your authenticator app.</p>
                    <p className="text-xs text-slate-400 mt-1">Can't scan? Enter this key manually:</p>
                    <code className="mt-1 inline-block font-pnr text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 break-all" data-testid="twofa-secret">{twoFA.secret}</code>
                    <p className="text-sm text-slate-600 mt-4">2. Enter the 6-digit code it shows:</p>
                    <div className="flex items-center gap-2 mt-2 max-w-xs">
                      <Input data-testid="twofa-code" inputMode="numeric" maxLength={6} value={twoFA.code} onChange={(e) => setTwoFA((s) => ({ ...s, code: e.target.value.replace(/\D/g, "") }))} placeholder="123456" className="text-center tracking-[0.4em] font-pnr" />
                      <Button data-testid="twofa-confirm" onClick={enable2FA} disabled={twoFA.busy || twoFA.code.length !== 6} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
                        {twoFA.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                      </Button>
                    </div>
                    <button onClick={() => setTwoFA({ open: false, uri: "", secret: "", code: "", busy: false })} className="mt-3 text-xs text-slate-500 hover:text-slate-800" data-testid="twofa-cancel">Cancel</button>
                  </div>
                </div>
              )}
            </section>

            {/* Change password */}
            <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="password-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><KeyRound className="h-4 w-4 text-slate-400" /> Password</h2>
                  <p className="text-sm text-slate-500 mt-1 max-w-md">Change the password you use to sign in. You'll need your current password to confirm.</p>
                </div>
                {!pw.open && (
                  <Button data-testid="change-password-open" onClick={() => setPw({ open: true, current: "", next: "", confirm: "", busy: false })} className="bg-slate-900 hover:bg-slate-800 text-white">
                    <KeyRound className="h-4 w-4 mr-1.5" /> Change password
                  </Button>
                )}
              </div>

              {pw.open && (
                <div className="mt-5 border-t border-slate-100 pt-5 max-w-sm space-y-3" data-testid="change-password-form">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Current password</label>
                    <Input data-testid="cp-current" type="password" value={pw.current} onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))} className="mt-1" placeholder="••••••••" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">New password</label>
                    <Input data-testid="cp-new" type="password" value={pw.next} onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))} className="mt-1" placeholder="At least 6 characters" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Confirm new password</label>
                    <Input data-testid="cp-confirm" type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} className="mt-1" placeholder="Re-enter new password" />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button data-testid="cp-submit" onClick={changePassword} disabled={pw.busy || !pw.current || !pw.next || !pw.confirm} className="bg-blue-600 hover:bg-blue-700 text-white">
                      {pw.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                    </Button>
                    <button data-testid="cp-cancel" onClick={() => setPw({ open: false, current: "", next: "", confirm: "", busy: false })} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
                  </div>
                </div>
              )}
            </section>

            {/* Transactions / billing history */}
            <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="transactions-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2"><Receipt className="h-4 w-4 text-slate-400" /> Billing history</h2>
                <span className="text-xs text-slate-400">{txns.length} transaction(s)</span>
              </div>
              {txns.length === 0 ? (
                <div data-testid="no-transactions" className="flex flex-col items-center justify-center text-center py-10 rounded-lg border border-dashed border-slate-200">
                  <FileText className="h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-sm font-medium text-slate-600">No transactions yet</div>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs">Upgrade to a paid plan and your charges &amp; invoices will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="transactions-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                        <th className="py-2.5 pr-4 font-medium">Date</th>
                        <th className="py-2.5 pr-4 font-medium">Plan</th>
                        <th className="py-2.5 pr-4 font-medium">Amount</th>
                        <th className="py-2.5 pr-4 font-medium">Card charged</th>
                        <th className="py-2.5 pr-4 font-medium">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.map((t) => (
                        <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b border-slate-50 last:border-0">
                          <td className="py-3 pr-4 text-slate-700">{fmtDate(t.date)}</td>
                          <td className="py-3 pr-4"><span className="inline-flex rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5">{t.plan_name}</span></td>
                          <td className="py-3 pr-4 font-medium text-slate-900">{t.currency} {Number(t.amount).toFixed(2)}</td>
                          <td className="py-3 pr-4 text-slate-600">{t.card_brand ? `${t.card_brand} •••• ${t.card_last4}` : "—"}</td>
                          <td className="py-3 pr-4">
                            <Button data-testid={`invoice-btn-${t.id}`} onClick={() => getInvoice(t)} disabled={invoiceBusy === t.id} variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                              {invoiceBusy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1.5" /> PDF</>}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {/* Manage plan modal */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-3xl" data-testid="manage-dialog">
          <DialogHeader><DialogTitle>Manage your plan</DialogTitle><DialogDescription>Switch plans, change your API tier or seats, and pay securely with Razorpay. Choose monthly or annual billing.</DialogDescription></DialogHeader>
          {billing && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm" data-testid="manage-summary">
              <span>Current: <b className="text-slate-900">{billing.plan_name}</b></span>
              <span data-testid="manage-status">Status: <b className={billing.cancel_at_period_end ? "text-amber-700" : "text-slate-900"}>{statusText}</b></span>
              <span data-testid="manage-next-billing">Next billing date: <b className="text-slate-900">{nextBilling || (billing.cancel_at_period_end ? "No further charge" : "—")}</b></span>
            </div>
          )}
          {billing && (
            <div className="flex items-center justify-center">
              <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="interval-toggle">
                <button data-testid="interval-monthly" onClick={() => setInterval("monthly")}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors ${interval === "monthly" ? "bg-white shadow text-slate-900 font-semibold" : "text-slate-500"}`}>Monthly</button>
                <button data-testid="interval-annual" onClick={() => setInterval("annual")}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors inline-flex items-center gap-1.5 ${interval === "annual" ? "bg-white shadow text-slate-900 font-semibold" : "text-slate-500"}`}>Annual <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded px-1.5 py-0.5">SAVE 25%</span></button>
              </div>
            </div>
          )}
          {billing && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((p) => {
                const meta = PLAN_META[p.id] || {};
                const Icon = meta.icon || Zap;
                const current = billing.plan === p.id;
                const intervalChanged = current && p.id !== "basic" && interval !== (billing.billing_interval || "monthly");
                const changed =
                  (p.id === "plus" && current && (users !== (billing.users || 1) || intervalChanged)) ||
                  (p.id === "api" && current && (apiTier !== (billing.api_tier || 0) || intervalChanged));
                const actionable = !current || changed;
                const plusMo = interval === "annual" ? 7.49 : 9.99;
                const apiMo = interval === "annual" ? (TIER_PRICES[apiTier] * 0.75) : TIER_PRICES[apiTier];
                let priceLabel = p.id === "plus" ? `${fmt(plusMo)}/user/mo` : p.id === "basic" ? fmt(0) : `${fmt(apiMo)}/mo`;
                return (
                  <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-lg border p-5 flex flex-col ${current && !changed ? "border-slate-200 bg-slate-50 opacity-60" : current ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
                    <div className="flex items-center justify-between"><Icon className="h-5 w-5 text-blue-600" />{current && <Badge data-testid={`plan-current-${p.id}`} className="bg-slate-500 text-white rounded-full">Subscribed</Badge>}</div>
                    <div className="mt-2 font-heading font-bold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{meta.blurb}</div>
                    <div className="mt-2 font-heading text-xl font-bold text-slate-900" data-testid={`plan-price-${p.id}`}>{priceLabel}</div>

                    {p.id === "plus" && (
                      <div className="mt-3 flex items-center justify-between rounded-md border border-slate-200 px-2 py-1.5">
                        <span className="text-xs text-slate-500">Users</span>
                        <div className="flex items-center gap-2">
                          <button data-testid="mp-users-dec" onClick={() => setUsers((u) => Math.max(1, u - 1))} className="h-6 w-6 rounded border border-slate-300 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                          <span data-testid="mp-users" className="w-6 text-center text-sm font-bold">{users}</span>
                          <button data-testid="mp-users-inc" onClick={() => setUsers((u) => Math.min(50, u + 1))} className="h-6 w-6 rounded border border-slate-300 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                        </div>
                      </div>
                    )}
                    {p.id === "plus" && (
                      <div className="text-[11px] text-slate-400">{interval === "annual" ? "billed annually · save 25%" : "billed monthly"}</div>
                    )}
                    {p.id === "api" && (
                      <div className="mt-3 space-y-1">
                        {p.tiers.map((t) => (
                          <button key={t.tier} data-testid={`mp-tier-${t.tier}`} onClick={() => setApiTier(t.tier)}
                            className={`w-full flex items-center justify-between rounded-md border px-2 py-1.5 text-xs ${apiTier === t.tier ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                            <span>{t.limit === null ? "Unlimited" : t.limit.toLocaleString()}</span>
                            <span className="font-semibold">{fmt(interval === "annual" ? t.annual_month : t.monthly)}/mo</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <ul className="mt-4 space-y-2 flex-1">
                      {(PLAN_FEATURES[p.id] || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-600" /> {f}</li>
                      ))}
                    </ul>
                    <Button data-testid={`select-plan-${p.id}`} disabled={!actionable || busy === p.id} onClick={() => checkout(p.id)}
                      className={`mt-4 ${actionable ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-200 text-slate-400"}`}>
                      {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : changed ? "Update plan" : current ? "Subscribed" : p.id === "basic" ? "Downgrade to Basic" : `Subscribe to ${p.name}`}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          {billing && billing.plan !== "basic" && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4 mt-2">
              <span className="text-sm text-slate-500">
                {billing.cancel_at_period_end ? `Cancelling at period end (${endDate}). Pick a plan above to reactivate.` : "Cancel to stop future charges; access stays until the period ends."}
              </span>
              {!billing.cancel_at_period_end && (
                <Button data-testid="cancel-sub-btn" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={cancel} disabled={busy === "cancel"}>
                  {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />} Cancel subscription
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
